// Orchestration service for rendering the project page with following tabs:
// - Details
// - Performance
// - Roster

import { Layout, Tabs, Button, Space, Typography } from "antd";
import { useState, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ReloadOutlined } from "@ant-design/icons";
import ProjectDetails from "./ProjectDetails";
import ProjectHeader from "./ProjectHeader";
import CloneProjectModal from "../home/CloneProjectModal";
import { colors } from "../../config/constants";
import PerformanceOrchestrator from "../performance/PerformanceOrchestrator";
import RosterOrchestrator from "../roster/RosterOrchestrator";
import {
  DashboardOutlined,
  InfoCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { getDefaultDateRange } from "../../utils/formatters";
import TimeframeSelector from "../performance/TimeframeSelector";

const { Text } = Typography;

const ProjectViewOrchestrator = () => {
  const { projectId: pId, projectObjectiveId: poId } = useParams();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") || "details");
  const [timeframeDateRange, setTimeframeDateRange] =
    useState(getDefaultDateRange);
  const [projectMetadata, setProjectMetadata] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [headerState, setHeaderState] = useState({
    saving: false,
    hasFormErrors: false,
    validating: false,
  });

  const detailsHandlersGetterRef = useRef(null);
  const rosterOrchestratorRef = useRef(null);
  const [rosterLastSyncedAt, setRosterLastSyncedAt] = useState(null);

  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    detailsHandlersGetterRef.current?.().resetEdit?.();
  }, []);
  const handleSave = useCallback(() => {
    detailsHandlersGetterRef.current?.().handleSave?.();
  }, []);
  const handleValidate = useCallback(() => {
    detailsHandlersGetterRef.current?.().handleValidate?.();
  }, []);
  const handleClone = useCallback(() => setShowCloneModal(true), []);
  const handleTogglePreview = useCallback(() => setShowPreview((v) => !v), []);

  const registerDetailsHandlers = useCallback((getter) => {
    detailsHandlersGetterRef.current = getter;
  }, []);

  const handleTimeframeDateRangeChange = useCallback((start, end) => {
    setTimeframeDateRange({ start, end });
  }, []);

  const getTabActions = () => {
    switch (view) {
      case "performance":
        return (
          <TimeframeSelector
            onDateRangeChange={handleTimeframeDateRangeChange}
          />
        );
      default:
        return;
    }
  };

  return (
    <Layout>
      <ProjectHeader
        projectName={projectMetadata?.name || "Unnamed Project"}
        isEditing={isEditing}
        saving={headerState.saving}
        hasFormErrors={headerState.hasFormErrors}
        validating={headerState.validating}
        showPreview={showPreview}
        onEdit={handleEdit}
        onCancelEdit={handleCancelEdit}
        onSave={handleSave}
        onValidate={handleValidate}
        onClone={handleClone}
        onTogglePreview={handleTogglePreview}
      />
      <Layout.Content style={{ position: "relative" }}>
        <Tabs
          defaultActiveKey={view}
          size="medium"
          styles={{
            header: {
              padding: "0 16px 0 32px",
              position: "sticky",
              top: 73,
              zIndex: 25,
              backgroundColor: colors.white,
              marginBottom: 0,
              borderBottom: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            },
          }}
          tabBarExtraContent={getTabActions()}
          onChange={(key) => setView(key)}
          activeKey={view}
          items={[
            {
              key: "details",
              label: "Details",
              icon: <InfoCircleOutlined />,
              children: (
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                  <ProjectDetails
                    projectId={pId}
                    poId={poId}
                    setProjectMetadata={setProjectMetadata}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    showCloneModal={showCloneModal}
                    setShowCloneModal={setShowCloneModal}
                    showPreview={showPreview}
                    setShowPreview={setShowPreview}
                    onHeaderStateChange={setHeaderState}
                    onRegisterHandlers={registerDetailsHandlers}
                  />
                </div>
              ),
            },
            {
              key: "performance",
              label: "Performance",
              icon: <DashboardOutlined />,
              children: (
                <PerformanceOrchestrator
                  projectId={pId}
                  objectiveId={poId}
                  timeframeDateRange={timeframeDateRange}
                />
              ),
            },
            {
              key: "roster",
              label: "Roster",
              icon: <TeamOutlined />,
              children: (
                <>
                  <div
                    style={{
                      marginBottom: 12,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => rosterOrchestratorRef.current?.refresh?.()}
                    >
                      Refresh
                    </Button>
                    <Text type="secondary">
                      {rosterLastSyncedAt
                        ? `Last refreshed at ${rosterLastSyncedAt.toLocaleString()}`
                        : "No data in cache. Click Refresh to load."}
                    </Text>
                  </div>
                  <RosterOrchestrator
                    ref={rosterOrchestratorRef}
                    projectId={pId}
                    objectiveId={poId}
                    onLastSyncedAtChange={setRosterLastSyncedAt}
                  />
                </>
              ),
            },
          ]}
        />
        <CloneProjectModal
          visible={showCloneModal}
          onClose={() => setShowCloneModal(false)}
          onSuccess={() => setShowCloneModal(false)}
          selectedProjectId={pId}
          selectedProjectName={projectMetadata?.name}
        />
      </Layout.Content>
    </Layout>
  );
};

export default ProjectViewOrchestrator;
